/* DATA EXPLORER — one visual on the canvas (2026-10-22): its header (title, drill controls, the … menu) and its body —
   a card, a table, a matrix, a chart or a slicer. Every body reads the same answer (the server's groups turned into a
   pivot by explorerModel.buildPivot), so the table under "Show as a table" is the chart's own numbers.

   A click on a bar or a row FILTERS THE OTHER VISUALS of the page (Power BI's cross-filter); the clicked visual keeps
   its picture and dims what was not picked; a second click clears it. With the drill-down mode on (the ↓ in the
   header) the same click goes one level deeper instead. */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area, ComposedChart, PieChart, Pie, XAxis, YAxis, Tooltip,
  Legend, CartesianGrid, Cell, ReferenceLine, LabelList,
} from 'recharts';
import {
  ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronUp, ChevronsDown, ChevronsDownUp, ChevronsUpDown, Copy, Download,
  Ellipsis, Loader2, Maximize2, Search, Table2, Trash2,
} from 'lucide-react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { createPortal } from 'react-dom';
import {
  MAP_FIELDS, OTHER_COLOR, REL_PERIODS, SERIES, SURFACE, VISUAL_TYPES, buildPivot, compact, defaultSort, fmtValue, keyLabel,
  headline, refOf, relLabel, sortRows, valueColors,
} from './explorerModel';

const PAGE_ROWS = 300;

/* ================================================================ the frame */
export const Visual = React.memo(function Visual({ v, answer, ctx, selected, geometry, xfKey, focus }) {
  const { t, editing, actions } = ctx;
  const [menu, setMenu] = useState(false);
  const [asTable, setAsTable] = useState(false);
  /* The matrix's folded levels, by the key of the level's values; empty = everything open. */
  const [folded, setFolded] = useState(() => new Set());
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    const key = (e) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', key); };
  }, [menu]);

  const pivot = useMemo(() => (answer?.result ? buildPivot(answer.result) : null), [answer]);
  /* The answer comes back without the names the values were given in this visual (the question goes without them),
     so they are laid back on by position — the visual's own values come first in every question. */
  const result = useMemo(() => {
    const r = answer?.result;
    if (!r) return r;
    const own = [...(v.values || []), ...(v.type === 'combo' ? v.values2 || [] : [])];
    if (!own.some((x) => x.label)) return r;
    return { ...r, values: r.values.map((x, i) => (i < own.length && own[i].label && !(v.type === 'kpi' && i > 0) ? { ...x, label: own[i].label } : x)) };
  }, [answer, v.values, v.values2, v.type]);
  const drills = (v.filters || []).filter((f) => f.drill);
  const canDrill = v.rows.length > 0 && !['slicer', 'card', 'kpi'].includes(v.type);
  /* A card names its figure under the number, so its header carries a title only when somebody typed one. */
  const fullTitle = v.title || ctx.autoTitle(v);
  const fmt = v.format || {};
  const title = (v.type === 'card' && !v.title) || fmt.showTitle === false ? '' : fullTitle;
  const isChart = VISUAL_TYPES[v.type]?.chart;
  const isRound = VISUAL_TYPES[v.type]?.round;
  const hasData = v.type === 'slicer' ? v.rows.length > 0 : v.values.length > 0;
  const levels = v.type === 'matrix' && pivot?.subs?.size > 0;
  const foldAll = () => setFolded(new Set([...pivot.subs.keys()]));

  let body;
  if (!hasData) {
    body = <div className="dx-vempty">{t('xExplorer.emptyVisual')}</div>;
  } else if (v.type === 'slicer') {
    body = <SlicerBody v={v} ctx={ctx} />;
  } else if (answer?.error) {
    body = <div className="dx-verr">{answer.error}</div>;
  } else if (!pivot) {
    body = <div className="dx-vwait"><Loader2 size={16} className="dx-spin" /></div>;
  } else if (v.type === 'card') {
    body = <CardBody pivot={pivot} result={result} ctx={ctx} fmt={fmt} />;
  } else if (v.type === 'kpi') {
    body = <KpiBody v={v} pivot={pivot} result={result} ctx={ctx} fmt={fmt} />;
  } else if (v.type === 'table' || v.type === 'matrix' || asTable) {
    body = <TableBody v={v} pivot={pivot} result={result} ctx={ctx} xfKey={xfKey} folded={folded} setFolded={setFolded} />;
  } else if (v.type === 'map') {
    body = <MapBody v={v} pivot={pivot} result={result} ctx={ctx} xfKey={xfKey} />;
  } else if (isRound) {
    body = <PieBody v={v} pivot={pivot} result={result} ctx={ctx} xfKey={xfKey} />;
  } else if (isChart) {
    body = <ChartBody v={v} pivot={pivot} result={result} ctx={ctx} xfKey={xfKey} />;
  }

  return (
    <div className={`dx-vis-box kind-${v.type}${title ? '' : ' untitled'}${selected ? ' sel' : ''}${editing ? ' editing' : ''}${focus ? ' focus' : ''}`}
      style={geometry} data-visual={v.id}
      onPointerDown={(e) => { if (!focus) actions.select(v.id, e); }}>
      <div className="dx-vhead" onPointerDown={(e) => { if (editing && !focus && !e.target.closest('button, select, input')) actions.startMove(e, v.id); }}>
        <span className="dx-vtitle" title={fullTitle}>{title}</span>
        {answer?.busy && hasData && <Loader2 size={13} className="dx-spin dx-vbusy" />}
        <span className="dx-vtools">
          {canDrill && (
            <>
              <button type="button" className="dx-vtool" disabled={!drills.length} title={t('xExplorer.drillUp')} aria-label={t('xExplorer.drillUp')}
                onClick={() => actions.drillUp(v.id)}><ArrowUp size={14} /></button>
              <button type="button" className={`dx-vtool${v.drillMode ? ' on' : ''}`} aria-pressed={!!v.drillMode}
                title={t('xExplorer.drillMode')} aria-label={t('xExplorer.drillMode')}
                onClick={() => actions.patch(v.id, { drillMode: !v.drillMode })}><ArrowDown size={14} /></button>
              <button type="button" className="dx-vtool" title={t('xExplorer.nextLevel')} aria-label={t('xExplorer.nextLevel')}
                onClick={() => actions.nextLevel(v.id)}><ChevronsDown size={14} /></button>
            </>
          )}
          <span className="dx-vmenu-wrap" ref={menuRef}>
            <button type="button" className="dx-vtool" aria-label={t('xExplorer.moreOptions')} title={t('xExplorer.moreOptions')}
              aria-expanded={menu} onClick={() => setMenu((m) => !m)}><Ellipsis size={15} /></button>
            {menu && (
              <div className="dx-vmenu" role="menu">
                {levels && (
                  <>
                    <button type="button" role="menuitem" onClick={() => { setMenu(false); setFolded(new Set()); }}>
                      <ChevronsUpDown size={14} /> {t('xExplorer.expandAll')}
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenu(false); foldAll(); }}>
                      <ChevronsDownUp size={14} /> {t('xExplorer.collapseAll')}
                    </button>
                  </>
                )}
                {(isChart || isRound || v.type === 'map') && (
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); setAsTable((x) => !x); }}>
                    <Table2 size={14} /> {asTable ? t('xExplorer.showVisual') : t('xExplorer.showTable')}
                  </button>
                )}
                {!focus && (
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); actions.focus(v.id); }}>
                    <Maximize2 size={14} /> {t('xExplorer.focus')}
                  </button>
                )}
                {pivot && v.type !== 'slicer' && (
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); actions.exportVisual(v, pivot, result, fullTitle); }}>
                    <Download size={14} /> {t('xExplorer.exportData')}
                  </button>
                )}
                {editing && (
                  <>
                    <button type="button" role="menuitem" onClick={() => { setMenu(false); actions.duplicate(v.id); }}>
                      <Copy size={14} /> {t('xExplorer.duplicate')}
                    </button>
                    <button type="button" role="menuitem" className="danger" onClick={() => { setMenu(false); actions.remove(v.id); }}>
                      <Trash2 size={14} /> {t('xExplorer.removeVisual')}
                    </button>
                  </>
                )}
              </div>
            )}
          </span>
        </span>
      </div>
      {drills.length > 0 && (
        <div className="dx-vcrumbs">{drills.map((f, i) => <span key={i}>{ctx.filterSummary(f)}</span>)}</div>
      )}
      <div className="dx-vbody">{body}</div>
      {editing && selected && !focus && (
        <span className="dx-resize" onPointerDown={(e) => { e.stopPropagation(); actions.startResize(e, v.id); }} aria-hidden="true" />
      )}
    </div>
  );
});

/* ================================================================ card */
const CardBody = React.memo(function CardBody({ pivot, result, ctx, fmt }) {
  const vals = result.values;
  const cur = result.params?.currency;
  return (
    <div className={`dx-cards n${Math.min(vals.length, 4)}`}>
      {vals.map((v, m) => {
        const n = pivot.grand?.[m];
        /* A card reads compactly unless the visual says otherwise; the hover always gives the whole figure. */
        const shown = fmt.units ? fmtValue(n, v, cur, fmt) : (v.fmt !== 'pct' && Math.abs(n) >= 100000 ? headline(n, v, cur) : fmtValue(n, v, cur));
        return (
          <div key={m} className="dx-card">
            {/* The name over the figure, as the Sales Report's tiles read: WEEK ACTUAL, then ₩842M. */}
            <div className="dx-card-l" title={ctx.valueLabel(v)}>{ctx.valueLabel(v)}</div>
            <div className={`dx-card-n${n < 0 ? ' neg' : ''}`} title={fmtValue(n, v, cur)}>
              {n === null || n === undefined ? (result.targetBlocked?.length ? '—' : '(Blank)') : shown}
            </div>
            {(n === null || n === undefined) && result.targetBlocked?.length > 0 && (
              <div className="dx-card-s">{ctx.t('xExplorer.targetBlockedShort', {
                fields: result.targetBlocked.map((k) => ctx.fieldLabel(k)).join(', '),
              })}</div>
            )}
          </div>
        );
      })}
    </div>
  );
});

/* ================================================================ table and matrix */
/* Conditional formatting reads each value on its own scale: a background on the blue sequential ramp (light → dark with
   the figure), or a data bar; a negative figure leans on the red end. Only detail cells are coloured — a total coloured
   on the same scale would always be the darkest and say nothing. */
const RAMP = {
  light: ['#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec', '#5598e7'],
  dark: ['#0d366b', '#104281', '#184f95', '#1c5cab', '#256abf', '#2a78d6'],
};
const NEG = { light: '#f6d2d2', dark: '#5a1f1f' };
const BAR = { light: '#86b6ef', dark: '#1c5cab' };
const cellStyle = (n, range, cond, mode) => {
  if (!cond || cond === 'none' || n === null || n === undefined || !range || !range.max) return undefined;
  const f = Math.min(1, Math.abs(n) / range.max);
  if (cond === 'bars') {
    const pct = Math.round(f * 100);
    const c = n < 0 ? NEG[mode] : BAR[mode];
    return { background: `linear-gradient(90deg, ${c} ${pct}%, transparent ${pct}%)` };
  }
  if (n < 0) return { background: NEG[mode] };
  return { background: RAMP[mode][Math.min(RAMP[mode].length - 1, Math.floor(f * RAMP[mode].length))] };
};

const TableBody = React.memo(function TableBody({ v, pivot, result, ctx, xfKey, folded, setFolded }) {
  const { t, actions } = ctx;
  const [shown, setShown] = useState(PAGE_ROWS);
  const sort = v.sort || defaultSort(v, ctx.fields);
  const fmt = v.format || {};
  const top = useMemo(() => (v.type === 'table' && pivot.R > 0 && !result.rows[0]?.grain ? topSet(pivot, fmt.topN) : null), [v.type, pivot, result.rows, fmt.topN]);
  const rows = useMemo(() => {
    const list = sortRows(pivot, sort);
    return top ? list.filter((r) => top.has(r.key)) : list;
  }, [pivot, sort, top]);
  const vals = result.values;
  const cur = result.params?.currency;
  const mode = ctx.theme === 'dark' ? 'dark' : 'light';
  const num = (n, m) => fmtValue(n, vals[m], cur, fmt);
  /* One value under the columns needs no row of value names: the column already says what it is, and its own head
     sorts it. */
  const oneVal = pivot.C > 0 && vals.length === 1;
  const twoHeads = pivot.C > 0 && !oneVal;
  /* A matrix with levels is drawn stepped, as Power BI's is: one row-header column, each level indented under its
     parent with the parent's subtotal on its own line, and +/− to fold a level. */
  const stepped = v.type === 'matrix' && pivot.R > 1 && pivot.subs?.size > 0;
  const tableRef = useRef(null);
  /* The second header row sticks under the first at the first row's MEASURED height. */
  useLayoutEffect(() => {
    const tb = tableRef.current;
    const first = tb?.tHead?.rows?.[0];
    if (!tb || !first) return;
    const cell = [...first.cells].find((c) => c.rowSpan === 1) || first.cells[0];
    if (cell) tb.style.setProperty('--dx-h1', `${cell.getBoundingClientRect().height}px`);
  });
  /* Each value's scale for conditional formatting: the largest absolute detail figure. */
  const ranges = useMemo(() => {
    if (!fmt.cond || fmt.cond === 'none') return null;
    return vals.map((_, m) => {
      let max = 0;
      for (const r of pivot.rows) {
        const arrs = pivot.C ? [...r.cells.values()] : [r.total];
        for (const a of arrs) { const x = a?.[m]; if (x !== null && x !== undefined) max = Math.max(max, Math.abs(x)); }
      }
      return { max };
    });
  }, [fmt.cond, vals, pivot]);
  const sortBy = (s) => {
    const same = sort.kind === s.kind && sort.idx === s.idx && sort.col === s.col && sort.m === s.m;
    actions.patch(v.id, { sort: { ...s, dir: same ? (sort.dir === 'asc' ? 'desc' : 'asc') : (s.kind === 'row' ? 'asc' : 'desc') } });
  };
  const mark = (s) => (sort.kind === s.kind && sort.idx === s.idx && sort.col === s.col && sort.m === s.m
    ? (sort.dir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />) : null);
  const colLabel = (c) => (c.vals.length ? c.vals.map((x, i) => keyLabel(result.cols[i], x)).join(' · ') : t('xExplorer.total'));
  const rowHead = stepped ? (
    <th rowSpan={twoHeads ? 2 : 1}>
      <button type="button" className="dx-th" onClick={() => sortBy({ kind: 'row', idx: 0 })}>
        {result.rows.map((d) => ctx.dimLabel(d)).join(' › ')} {mark({ kind: 'row', idx: 0 })}
      </button>
    </th>
  ) : result.rows.map((d, i) => (
    <th key={`r${i}`} rowSpan={twoHeads ? 2 : 1}>
      <button type="button" className="dx-th" onClick={() => sortBy({ kind: 'row', idx: i })}>{ctx.dimLabel(d)} {mark({ kind: 'row', idx: i })}</button>
    </th>
  ));
  const valHead = (col) => vals.map((x, m) => (
    <th key={`${col || 't'}-${m}`} className="num">
      <button type="button" className="dx-th num" onClick={() => sortBy({ kind: 'val', col, m })}>{ctx.valueLabel(x)} {mark({ kind: 'val', col, m })}</button>
    </th>
  ));
  const colHead = (key, label, tot) => (
    <th key={key || 'tot'} className={`num dx-ch${tot ? ' tot' : ''}`}>
      <button type="button" className="dx-th num" onClick={() => sortBy({ kind: 'val', col: key, m: 0 })}>{label} {mark({ kind: 'val', col: key, m: 0 })}</button>
    </th>
  );
  const cells = (arr, detail) => vals.map((_, m) => (
    <td key={m} className={`num${arr?.[m] < 0 ? ' neg' : ''}`} style={detail ? cellStyle(arr?.[m], ranges?.[m], fmt.cond, mode) : undefined}>
      {num(arr?.[m], m)}
    </td>
  ));
  const figures = (node, detail) => (pivot.C ? (
    <>
      {pivot.cols.map((c) => <React.Fragment key={c.key}>{cells(node.cells?.get(c.key), detail)}</React.Fragment>)}
      {cells(node.total, false)}
    </>
  ) : cells(node.total, detail));
  const clickRow = (vals0, dims, level) => (v.drillMode
    ? actions.drill(v.id, level, vals0[level])
    : actions.point(v.id, vals0, dims));

  /* The stepped tree: the leaves keep the sort's order inside their parent; parents sort by the same rule, read on
     their own subtotal (or their own label). */
  const lines = useMemo(() => {
    if (!stepped) return null;
    const R = pivot.R;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const valOf = (node) => {
      if (sort.kind === 'row') return node.vals[node.vals.length - 1];
      const arr = sort.col ? node.cells?.get(sort.col) : node.total;
      return arr ? arr[sort.m ?? 0] : null;
    };
    const cmp = (a, b) => {
      const x = valOf(a);
      const y = valOf(b);
      if (x === null || x === undefined) return (y === null || y === undefined) ? 0 : 1;
      if (y === null || y === undefined) return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return String(x).localeCompare(String(y), 'en', { numeric: true }) * (sort.kind === 'row' ? dir : 1);
    };
    const out = [];
    const walk = (leaves, level) => {
      if (level === R - 1) {
        for (const r of leaves) out.push({ key: r.key, vals: r.vals, level, node: r, leaf: true });
        return;
      }
      const groups = new Map();
      for (const r of leaves) {
        const k = JSON.stringify(r.vals.slice(0, level + 1));
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(r);
      }
      const nodes = [...groups.entries()].map(([k, list]) => {
        const sub = pivot.subs.get(k) || { cells: new Map(), total: null };
        return { key: k, vals: list[0].vals.slice(0, level + 1), level, node: sub, list, total: sub.total, cells: sub.cells };
      }).sort(cmp);
      for (const n of nodes) {
        out.push({ key: n.key, vals: n.vals, level, node: n.node, leaf: false });
        if (!folded.has(n.key)) walk(n.list, level + 1);
      }
    };
    walk(rows, 0);
    return out;
  }, [stepped, rows, pivot, sort, folded]);

  const toggle = (k) => setFolded((s0) => { const s1 = new Set(s0); if (s1.has(k)) s1.delete(k); else s1.add(k); return s1; });
  const list = stepped ? lines : rows;
  return (
    <div className="dx-tablewrap">
      <table className={`dx-table${stepped ? ' stepped' : ''}`} ref={tableRef}>
        <thead>
          {twoHeads ? (
            <>
              <tr>
                {rowHead}
                {pivot.cols.map((c) => <th key={c.key} colSpan={vals.length} className="dx-ch">{colLabel(c)}</th>)}
                <th colSpan={vals.length} className="dx-ch tot">{t('xExplorer.total')}</th>
              </tr>
              <tr>{pivot.cols.map((c) => valHead(c.key))}{valHead(null)}</tr>
            </>
          ) : oneVal ? (
            <tr>{rowHead}{pivot.cols.map((c) => colHead(c.key, colLabel(c)))}{colHead(null, t('xExplorer.total'), true)}</tr>
          ) : <tr>{rowHead}{valHead(null)}</tr>}
        </thead>
        <tbody>
          {list.slice(0, shown).map((r) => {
            const picked = xfKey && xfKey === r.key;
            const cls = [xfKey ? (picked ? 'picked' : 'dim') : '', stepped && !r.leaf ? `dx-lvl lvl${r.level}` : ''].filter(Boolean).join(' ');
            if (stepped) {
              const level = r.level;
              const label = keyLabel(result.rows[level], r.vals[level]);
              const dims = result.rows.slice(0, level + 1);
              return (
                <tr key={r.key} className={cls || undefined}>
                  <td className="dx-rl" style={{ paddingLeft: 6 + level * 16 }}>
                    {!r.leaf ? (
                      <button type="button" className="dx-fold" aria-expanded={!folded.has(r.key)}
                        aria-label={`${folded.has(r.key) ? t('xExplorer.expand') : t('xExplorer.collapse')}: ${label}`}
                        onClick={() => toggle(r.key)}>
                        {folded.has(r.key) ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                      </button>
                    ) : <span className="dx-fold-sp" />}
                    <button type="button" className="dx-cellbtn" title={v.drillMode ? t('xExplorer.drillInto', { value: label }) : label}
                      onClick={() => clickRow(r.vals, dims, level)}>{label}</button>
                  </td>
                  {figures(r.node, r.leaf)}
                </tr>
              );
            }
            return (
              <tr key={r.key} className={cls || undefined}>
                {r.vals.map((x, i) => {
                  const label = keyLabel(result.rows[i], x);
                  return (
                    <td key={i} className="dx-rl">
                      <button type="button" className="dx-cellbtn" title={v.drillMode ? t('xExplorer.drillInto', { value: label }) : label}
                        onClick={() => (v.drillMode ? actions.drill(v.id, i, x) : actions.point(v.id, r.vals, result.rows))}>{label}</button>
                    </td>
                  );
                })}
                {figures(r, true)}
              </tr>
            );
          })}
        </tbody>
        {pivot.R > 0 && (
          <tfoot>
            <tr>
              <th colSpan={stepped ? 1 : pivot.R}>{t('xExplorer.total')}</th>
              {pivot.C ? (
                <>
                  {pivot.cols.map((c) => <React.Fragment key={c.key}>{cells(c.total, false)}</React.Fragment>)}
                  {cells(pivot.grand, false)}
                </>
              ) : cells(pivot.grand, false)}
            </tr>
          </tfoot>
        )}
      </table>
      {list.length > shown && (
        <button type="button" className="dx-link dx-morerows" onClick={() => setShown((n) => n + PAGE_ROWS)}>
          {t('xExplorer.moreRows', { n: Math.min(PAGE_ROWS, list.length - shown) })}
        </button>
      )}
      {top && <div className="dx-vnote">{t('xExplorer.topNoteTable', { n: rows.length, total: pivot.rows.length.toLocaleString('en-US') })}</div>}
      {(answerNote(ctx, result, pivot))}
    </div>
  );
});
const answerNote = (ctx, result, pivot) => {
  const notes = [];
  if (result.truncated) notes.push(ctx.t('xExplorer.truncated', { n: result.cap.toLocaleString('en-US') }));
  if (pivot.colsTotal) notes.push(ctx.t('xExplorer.tooManyCols', { n: 60, total: pivot.colsTotal }));
  /* A target asked for where it cannot be placed reads empty, and says why rather than leaving a blank to puzzle over. */
  if (result.targetBlocked?.length) {
    notes.push(ctx.t('xExplorer.targetBlocked', {
      fields: result.targetBlocked.map((k) => (k === '(dataset)' ? '—' : ctx.fieldLabel(k))).join(', '),
    }));
  }
  return notes.length ? <div className="dx-vnote">{notes.join(' ')}</div> : null;
};

/* TOP N (the Format tab): the N largest by the first value, kept in the visual's own order; the rest are left out, and
   the note says how many. A time axis is never cut this way — the last N periods are a date filter. */
const topSet = (pivot, n) => {
  if (!n || pivot.rows.length <= n) return null;
  const ranked = [...pivot.rows].sort((a, b) => (b.total?.[0] ?? -Infinity) - (a.total?.[0] ?? -Infinity));
  return new Set(ranked.slice(0, n).map((r) => r.key));
};

/* ================================================================ charts */
/* The width a chart has, for what recharts cannot size by itself (a category axis's own width). */
const useBoxWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setW(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};
/* A category name on two lines at most, broken between words, the second cut with an ellipsis; the whole name on the
   hover. */
const WrapTick = ({ x, y, payload, width }) => {
  const text = String(payload?.value ?? '');
  const per = Math.max(8, Math.floor((width - 10) / 6.1));
  const words = text.split(/\s+/);
  const lines = [''];
  for (const w of words) {
    const cur = lines[lines.length - 1];
    if (!cur) lines[lines.length - 1] = w;
    else if (`${cur} ${w}`.length <= per) lines[lines.length - 1] = `${cur} ${w}`;
    else lines.push(w);
  }
  let shown = lines.slice(0, 2);
  if (lines.length > 2 || shown.some((l) => l.length > per)) {
    shown = shown.map((l, i) => (l.length > per || (i === 1 && lines.length > 2) ? `${l.slice(0, per - 1).trimEnd()}…` : l));
  }
  const dy = shown.length > 1 ? -6 : 0;
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{text}</title>
      <text className="recharts-cartesian-axis-tick-value" x={-6} y={0} dy={dy} textAnchor="end" fontSize={11}
        fill="var(--portal-text-muted)" dominantBaseline="central">
        {shown.map((l, i) => <tspan key={i} x={-6} dy={i ? 13 : 0}>{l}</tspan>)}
      </text>
    </g>
  );
};

const ChartBody = React.memo(function ChartBody({ v, pivot, result, ctx, xfKey }) {
  const { t, theme, actions } = ctx;
  const [boxRef, boxW] = useBoxWidth();
  const mode = theme === 'dark' ? 'dark' : 'light';
  const fmt = v.format || {};
  /* A chart of one series may take another of the palette's hues; with several, the order is the validated one. */
  const base = SERIES[mode];
  const palette = Number.isInteger(fmt.color) ? [base[fmt.color], ...base.filter((_, i) => i !== fmt.color)] : base;
  const surface = SURFACE[mode];
  const sort = v.sort || defaultSort(v, ctx.fields);
  const sorted = useMemo(() => sortRows(pivot, sort), [pivot, sort]);
  /* A time axis reads in time order whatever the sort; anything else follows the visual's sort. */
  const timeFirst = !!result.rows[0]?.grain;
  const ordered = timeFirst ? [...sorted].sort((a, b) => String(a.vals[0]).localeCompare(String(b.vals[0]), 'en', { numeric: true })) : sorted;
  const limit = v.type === 'line' || v.type === 'area' ? 240 : 40;
  const top = !timeFirst && pivot.R ? topSet(pivot, fmt.topN) : null;
  const kept = top ? ordered.filter((r) => top.has(r.key)) : ordered;
  const cats = pivot.R ? kept.slice(0, limit) : [{ key: '[]', vals: [], cells: pivot.rows[0]?.cells || new Map(), total: pivot.grand }];
  let series;
  if (pivot.C) {
    const byTotal = [...pivot.cols].sort((a, b) => Math.abs(b.total?.[0] || 0) - Math.abs(a.total?.[0] || 0));
    const keepSet = new Set((byTotal.length > 8 ? byTotal.slice(0, 7) : byTotal).map((c) => c.key));
    series = pivot.cols.filter((c) => keepSet.has(c.key)).map((c, i) => ({
      id: `s${i}`, name: c.vals.map((x, j) => keyLabel(result.cols[j], x)).join(' · '), color: palette[i], cols: [c.key],
    }));
    if (byTotal.length > 8) {
      series.push({ id: 'other', name: t('xExplorer.other'), color: OTHER_COLOR[mode], cols: pivot.cols.filter((c) => !keepSet.has(c.key)).map((c) => c.key) });
    }
  } else {
    /* Several values without a legend are several series on ONE axis — fine when they share a unit (revenue and its
       average), which is the user's choice to make; the first value is the default. */
    const colors = valueColors(result.values, mode, palette);
    series = result.values.map((x, m) => ({ id: `m${m}`, name: ctx.valueLabel(x), color: colors[m], m, ref: refOf(x) }));
  }
  /* A combo draws its first values as columns and the rest as lines, on the one axis. */
  const nCols = v.type === 'combo' ? v.values.length : 0;
  const data = cats.map((r) => {
    const o = { cat: pivot.R ? r.vals.map((x, i) => keyLabel(result.rows[i], x)).join(' · ') : t('xExplorer.total'), __row: r };
    for (const s of series) {
      if (s.m !== undefined) o[s.id] = r.total?.[s.m] ?? null;
      else {
        let sum = null;
        for (const k of s.cols) { const x = r.cells.get(k)?.[0]; if (x !== null && x !== undefined) sum = (sum || 0) + x; }
        o[s.id] = sum;
      }
    }
    return o;
  });
  const cur = result.params?.currency;
  const valOfSeries = (s) => result.values[s?.m ?? 0];
  const pctAxis = result.values.every((x) => x.fmt === 'pct');
  const axisFmt = (x) => (pctAxis ? `${Math.round(x * 100)}%` : compact(x));
  const stacked = v.type === 'stacked' || (v.type === 'area' && pivot.C > 0);
  const horizontal = v.type === 'bar';
  const pick = (row) => {
    if (!row || !pivot.R) return;
    if (v.drillMode && pivot.R === 1) actions.drill(v.id, 0, row.vals[0]);
    else actions.point(v.id, row.vals, result.rows);
  };
  /* A bar is clicked on the bar itself: recharts 3 reads a chart-level click from its hover state, which follows the
     mouse a frame late, so a quick click (or a tap, which has no hover at all) would carry no point. A line or an area
     has no shape to click, so there the chart's own click, through the hovered category, does it. */
  /* In a combo the bars AND the chart listen, and a press on a bar reaches both — the bar's own click wins and the
     chart's, a moment later, is ignored, or the point would be picked and cleared by one press. */
  const barHit = useRef(0);
  const onChartClick = (st) => {
    if (Date.now() - barHit.current < 250) return;
    const i = Number(st?.activeTooltipIndex ?? st?.activeIndex);
    if (Number.isInteger(i) && data[i]) pick(data[i].__row);
  };
  const onBarClick = (entry) => { barHit.current = Date.now(); pick(entry?.payload?.__row); };
  /* Not picked = dimmed; on the dark surface a little less, or the unpicked bars all but vanish. */
  const dimOf = (row) => (xfKey && row.key !== xfKey ? (mode === 'dark' ? 0.45 : 0.3) : 1);
  const tick = { fontSize: 11, fill: 'var(--portal-text-muted)' };
  const cut = (n) => (x) => (String(x).length > n ? `${String(x).slice(0, n - 1)}…` : x);
  const grid = <CartesianGrid vertical={horizontal} horizontal={!horizontal} stroke="var(--portal-border)" strokeDasharray="3 3" />;
  /* A horizontal bar chart's names are what it is read by ("Naver Co., Ltd. (Store Farm)", "10003700 - Sunset Chair
     (re) - Black"), so they get a third of the box, up to 220px, and wrap onto a second line before they are cut. */
  const labelW = Math.round(Math.min(220, Math.max(96, boxW * 0.33)));
  const axes = horizontal ? (
    <>
      <XAxis type="number" tickFormatter={axisFmt} tick={tick} />
      <YAxis type="category" dataKey="cat" width={labelW} interval={0} tick={(p) => <WrapTick {...p} width={labelW} />} />
    </>
  ) : (
    <>
      <XAxis dataKey="cat" tick={tick} interval="preserveStartEnd" minTickGap={6} tickFormatter={cut(18)} />
      <YAxis tickFormatter={axisFmt} tick={tick} width={50} />
    </>
  );
  const tip = (
    <Tooltip formatter={(x, n, item) => [fmtValue(x, valOfSeries(series.find((q) => q.id === item?.dataKey)), cur, fmt), n]}
      cursor={{ fill: 'var(--portal-surface-hover)' }}
      allowEscapeViewBox={{ x: false, y: true }} wrapperStyle={{ zIndex: 30 }}
      /* A horizontal bar chart spends 110px on its labels, so its plot is often narrower than the tooltip; the tooltip
         starts at the chart's left edge there, over the labels, instead of running out of the visual. */
      position={horizontal ? { x: 4 } : undefined}
      contentStyle={{ background: 'var(--portal-surface)', border: '1px solid var(--portal-border)', borderRadius: 8, fontSize: 12, maxWidth: 220, whiteSpace: 'normal' }}
      labelStyle={{ color: 'var(--portal-text)', fontWeight: 600 }} itemStyle={{ color: 'var(--portal-text)' }} />
  );
  /* The legend's words wear the text colour; the swatch carries the series colour. */
  const legendAt = fmt.legend || 'bottom';
  const legend = series.length > 1 && legendAt !== 'none'
    ? (
      <Legend wrapperStyle={{ fontSize: 11 }} formatter={(x) => <span style={{ color: 'var(--portal-text)' }}>{x}</span>} itemSorter={null}
        verticalAlign={legendAt === 'top' ? 'top' : legendAt === 'right' ? 'middle' : 'bottom'}
        align={legendAt === 'right' ? 'right' : 'center'} layout={legendAt === 'right' ? 'vertical' : 'horizontal'} />
    ) : null;
  /* Data labels: the figure on the mark, compact, in the text ink — never the series colour. A stack carries one label,
     its TOTAL, on top: a segment's own figure written above the stack would read as the whole. */
  const labelText = (x, s) => (x === null || x === undefined ? '' : (valOfSeries(s)?.fmt === 'pct' ? fmtValue(x, valOfSeries(s), cur, fmt) : compact(x)));
  const labelFor = (s, pos) => (fmt.labels ? (
    <LabelList dataKey={s.id} position={pos} fontSize={10} fill="var(--portal-text-muted)" formatter={(x) => labelText(x, s)} />
  ) : null);
  const stackLabel = (s) => (fmt.labels ? (
    <LabelList position="top" fontSize={10} fill="var(--portal-text-muted)"
      valueAccessor={(e) => {
        const p = e?.payload || {};
        let sum = null;
        for (const q of series) { const x = p[q.id]; if (x !== null && x !== undefined) sum = (sum || 0) + x; }
        return sum;
      }}
      formatter={(x) => labelText(x, s)} />
  ) : null);
  const lineLike = v.type === 'line' || v.type === 'area';
  /* A figure written at the end of a horizontal bar needs room past the longest bar, or it is cut at the edge. */
  const common = {
    /* A figure written on top of the tallest column needs room above it too. */
    data, margin: { top: fmt.labels && !horizontal ? 18 : 6, right: horizontal && fmt.labels ? 44 : 12, left: 0, bottom: 0 },
    ...(lineLike ? { onClick: onChartClick, style: pivot.R ? { cursor: 'pointer' } : undefined } : {}),
  };
  const picked = xfKey ? data.find((d) => d.__row.key === xfKey)?.cat : null;
  let chart;
  if (v.type === 'combo') {
    chart = (
      <ComposedChart {...common} onClick={onChartClick} style={pivot.R ? { cursor: 'pointer' } : undefined} barCategoryGap="18%">
        {grid}{axes}{tip}{legend}
        {series.map((s, i) => (i < nCols ? (
          <Bar key={s.id} dataKey={s.id} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}
            onClick={pivot.R ? onBarClick : undefined}>
            {data.map((d) => <Cell key={d.__row.key} fillOpacity={dimOf(d.__row)} />)}
            {labelFor(s, 'top')}
          </Bar>
        ) : (
          <Line key={s.id} type="linear" dataKey={s.id} name={s.name} stroke={s.color} strokeWidth={2}
            strokeDasharray={s.ref ? '6 4' : undefined}
            dot={{ r: 3.5, stroke: surface, strokeWidth: 2, fill: s.color }} isAnimationActive={false}>
            {labelFor(s, 'top')}
          </Line>
        )))}
      </ComposedChart>
    );
  } else if (lineLike) {
    const Shape = v.type === 'line' ? Line : Area;
    const Chart = v.type === 'line' ? LineChart : AreaChart;
    chart = (
      <Chart {...common}>
        {grid}{axes}{tip}{legend}
        {picked && <ReferenceLine x={picked} stroke="var(--portal-text-muted)" strokeDasharray="4 3" />}
        {series.map((s) => (
          <Shape key={s.id} type="linear" dataKey={s.id} name={s.name} stroke={s.color} strokeWidth={2}
            strokeDasharray={s.ref && v.type === 'line' ? '6 4' : undefined}
            fill={s.color} fillOpacity={v.type === 'area' ? 0.25 : undefined} stackId={stacked ? 'st' : undefined}
            dot={false} activeDot={{ r: 4, stroke: surface, strokeWidth: 2 }} connectNulls={false} isAnimationActive={false}>
            {labelFor(s, 'top')}
          </Shape>
        ))}
      </Chart>
    );
  } else {
    chart = (
      <BarChart {...common} layout={horizontal ? 'vertical' : 'horizontal'} barCategoryGap="18%">
        {grid}{axes}{tip}{legend}
        {series.map((s, i) => {
          const last = i === series.length - 1;
          const radius = horizontal ? (stacked && !last ? 0 : [0, 4, 4, 0]) : (stacked && !last ? 0 : [4, 4, 0, 0]);
          return (
            <Bar key={s.id} dataKey={s.id} name={s.name} fill={s.color} stackId={stacked ? 'st' : undefined}
              stroke={surface} strokeWidth={stacked ? 1 : 0} radius={radius} maxBarSize={48} isAnimationActive={false}
              onClick={pivot.R ? onBarClick : undefined} style={pivot.R ? { cursor: 'pointer' } : undefined}>
              {data.map((d) => <Cell key={d.__row.key} fillOpacity={dimOf(d.__row)} />)}
              {stacked ? (last ? stackLabel(s) : null) : labelFor(s, horizontal ? 'right' : 'top')}
            </Bar>
          );
        })}
      </BarChart>
    );
  }
  return (
    <div className="dx-chartbox">
      {/* not 10×10: recharts measures the legend on its first draw and keeps that height — at 10px wide every item
          wrapped to a line of its own and a five-series chart lost two thirds of its box to empty legend space (24/10) */}
      <div className="dx-chartarea" ref={boxRef}><ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 800, height: 300 }}>{chart}</ResponsiveContainer></div>
      {pivot.R > 0 && top && <div className="dx-vnote">{t('xExplorer.topNote', { n: kept.length, total: ordered.length.toLocaleString('en-US') })}</div>}
      {pivot.R > 0 && !top && ordered.length > limit && <div className="dx-vnote">{t('xExplorer.chartTop', { n: limit, total: ordered.length.toLocaleString('en-US') })}</div>}
      {result.targetBlocked?.length > 0 && answerNote(ctx, { targetBlocked: result.targetBlocked }, {})}
    </div>
  );
});

/* ================================================================ map */
/* A FILLED MAP (2026-10-22, Max: "dataexplore … thiếu biểu đồ map"): each country shaded on the blue sequential ramp by
   its figure — light for little, dark for much — in five classes cut at the quantiles, because sales are so lopsided
   (Korea and the US hold most of it) that equal steps would put every other country in the lightest one. With a region
   or a subregion as the location, every country of it wears the region's own total (worked out by the database as a
   subtotal), and a click picks the region. Places too small for the 1:110m world file (Singapore, Hong Kong, Macao…)
   are dots at their own coordinates. A name the map does not know is listed under it, never dropped silently. */
const GEO_URL = '/world-110m.json';
/* The sales table's spelling → the map file's. */
const MAP_ALIAS = {
  'United States': 'United States of America', USA: 'United States of America', 'Viet Nam': 'Vietnam', 'Türkiye': 'Turkey',
  'Bosnia and Herzegovina': 'Bosnia and Herz.', 'Brunei Darussalam': 'Brunei', 'Russian Federation': 'Russia',
  'North Macedonia': 'Macedonia', 'Czech Republic': 'Czechia', Korea: 'South Korea', 'Republic of Korea': 'South Korea',
  UK: 'United Kingdom', 'Dominican Republic': 'Dominican Rep.', 'Central African Republic': 'Central African Rep.',
  'Democratic Republic of the Congo': 'Dem. Rep. Congo', Eswatini: 'eSwatini', 'Solomon Islands': 'Solomon Is.',
  'Equatorial Guinea': 'Eq. Guinea', 'South Sudan': 'S. Sudan', "Cote d'Ivoire": "Côte d'Ivoire", 'Lao PDR': 'Laos',
  Iran: 'Iran', 'Syrian Arab Republic': 'Syria', 'Bolivia (Plurinational State of)': 'Bolivia',
};
const MAP_DOTS = {
  Singapore: [103.82, 1.35], 'Hong Kong': [114.17, 22.32], Macao: [113.55, 22.2], Macau: [113.55, 22.2],
  Jersey: [-2.13, 49.21], Guernsey: [-2.58, 49.45], 'Isle of Man': [-4.55, 54.24], Malta: [14.4, 35.9],
  Andorra: [1.52, 42.51], Monaco: [7.42, 43.74], Gibraltar: [-5.35, 36.14], Liechtenstein: [9.55, 47.16],
  'San Marino': [12.46, 43.94], Bahrain: [50.56, 26.07], Maldives: [73.22, 3.2], Mauritius: [57.55, -20.35],
  'Sint Maarten (Dutch part)': [-63.05, 18.04], 'Réunion': [55.53, -21.12], 'Cayman Islands': [-81.25, 19.31],
  Bermuda: [-64.75, 32.31], Barbados: [-59.54, 13.19], Guam: [144.79, 13.44], 'French Polynesia': [-149.4, -17.68],
};
/* The ramp starts a step in from its faintest blue: beside the grey of "no value", step 100 read as the same colour
   (measured on the first screenshot — Japan's little week looked unsold). */
const MAP_RAMP = {
  light: ['#9ec5f4', '#6da7ec', '#3987e5', '#1c5cab', '#0d366b'],
  dark: ['#184f95', '#256abf', '#3987e5', '#6da7ec', '#b7d3f6'],
};
const MAP_NONE = { light: '#e9ebee', dark: '#2a2e35' };
const MAP_EDGE = { light: '#ffffff', dark: '#121212' };
/* The world file is read once for the whole page, however many maps are on it. */
let worldPromise = null;
const loadWorld = () => {
  if (!worldPromise) {
    worldPromise = fetch(GEO_URL).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`map ${r.status}`))))
      .catch((e) => { worldPromise = null; throw e; });
  }
  return worldPromise;
};
const quantileBreaks = (list, k) => {
  const xs = [...new Set(list.filter((x) => Number.isFinite(x) && x > 0))].sort((a, b) => a - b);
  if (!xs.length) return [];
  const n = Math.min(k, xs.length);
  return Array.from({ length: n - 1 }, (_, i) => xs[Math.floor(((i + 1) * xs.length) / n)]);
};

const MapBody = React.memo(function MapBody({ v, pivot, result, ctx, xfKey }) {
  const { t, theme, actions } = ctx;
  const mode = theme === 'dark' ? 'dark' : 'light';
  const [world, setWorld] = useState(null);
  const [worldErr, setWorldErr] = useState('');
  const [tip, setTip] = useState(null);
  const boxRef = useRef(null);
  useEffect(() => {
    let live = true;
    loadWorld().then((w) => { if (live) setWorld(w); }).catch((e) => { if (live) setWorldErr(e.message); });
    return () => { live = false; };
  }, []);
  const loc = result.rows[0];
  const byArea = result.rows.length === 2;
  const val = result.values[0];
  const cur = result.params?.currency;
  const fmt = v.format || {};
  const names = useMemo(() => new Set((world?.objects?.countries?.geometries || []).map((g) => g.properties?.name)), [world]);
  const model = useMemo(() => {
    const shapes = new Map();
    const dots = [];
    const unplaced = [];
    const shadeOf = (area) => (byArea ? pivot.subs?.get(JSON.stringify([area]))?.total?.[0] ?? null : null);
    for (const r of pivot.rows) {
      const area = r.vals[0];
      const country = byArea ? r.vals[1] : r.vals[0];
      const own = r.total?.[0] ?? null;
      const info = { area, country, own, shade: byArea ? shadeOf(area) : own, key: JSON.stringify([area]) };
      if (MAP_DOTS[country]) dots.push({ ...info, at: MAP_DOTS[country] });
      else if (names.has(MAP_ALIAS[country] || country)) shapes.set(MAP_ALIAS[country] || country, info);
      else if (world) unplaced.push(info);
    }
    const shades = byArea ? [...new Set([...shapes.values(), ...dots].map((x) => x.shade))] : [...shapes.values(), ...dots].map((x) => x.shade);
    const breaks = quantileBreaks(shades, 5);
    return { shapes, dots, unplaced, breaks };
  }, [pivot, byArea, names, world]);
  if (!loc || !MAP_FIELDS.includes(loc.field)) return <div className="dx-vempty">{t('xExplorer.mapNeedsGeo')}</div>;
  if (worldErr) return <div className="dx-verr">{worldErr}</div>;
  if (!world) return <div className="dx-vwait"><Loader2 size={16} className="dx-spin" /></div>;
  const ramp = MAP_RAMP[mode];
  const classOf = (x) => {
    if (x === null || x === undefined || !(x > 0)) return -1;
    let c = 0;
    while (c < model.breaks.length && x >= model.breaks[c]) c += 1;
    /* Fewer than five distinct figures use the darker end of the ramp rather than its faint first steps. */
    return c + (ramp.length - 1 - model.breaks.length);
  };
  const fillOf = (x) => { const c = classOf(x); return c < 0 ? MAP_NONE[mode] : ramp[c]; };
  const dim = (info) => (xfKey && info.key !== xfKey ? 0.35 : 1);
  const pick = (info) => {
    if (v.drillMode) actions.drill(v.id, 0, info.area);
    else actions.point(v.id, [info.area], [loc]);
  };
  const show = (e, info) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (box) setTip({ x: e.clientX - box.left, y: e.clientY - box.top, info, w: box.width });
  };
  const figure = (n) => (n === null || n === undefined ? '—' : fmtValue(n, val, cur, fmt));
  const legendCuts = [0, ...model.breaks];
  const cut = (x) => (val?.fmt === 'pct' ? fmtValue(x, val, cur, { decimals: 0 }) : compact(x));
  return (
    <div className="dx-mapbox" ref={boxRef} onMouseLeave={() => setTip(null)}>
      <div className="dx-maparea">
        <ComposableMap projection="geoEqualEarth" projectionConfig={{ scale: 142 }} width={800} height={390}
          style={{ width: '100%', height: '100%' }}>
          {/* Ctrl + wheel zooms and a drag pans; a plain wheel keeps scrolling the page. The world without Antarctica,
              framed from Greenland to Cape Horn. */}
          <ZoomableGroup center={[12, 16]} zoom={1} minZoom={1} maxZoom={8}
            filterZoomEvent={(e) => e.type !== 'wheel' || e.ctrlKey}>
            <Geographies geography={world}>
              {({ geographies }) => geographies.filter((g) => g.properties?.name !== 'Antarctica').map((g) => {
                const info = model.shapes.get(g.properties?.name);
                return (
                  <Geography key={g.rsmKey} geography={g} data-name={g.properties?.name} fill={info ? fillOf(info.shade) : MAP_NONE[mode]}
                    fillOpacity={info ? dim(info) : 1} stroke={MAP_EDGE[mode]} strokeWidth={0.5}
                    onMouseMove={info ? (e) => show(e, info) : undefined} onMouseLeave={() => setTip(null)}
                    onClick={info ? () => pick(info) : undefined}
                    style={{
                      default: { outline: 'none', cursor: info ? 'pointer' : 'default' },
                      hover: { outline: 'none', cursor: info ? 'pointer' : 'default', ...(info ? { stroke: 'var(--portal-text)', strokeWidth: 0.9 } : {}) },
                      pressed: { outline: 'none' },
                    }} />
                );
              })}
            </Geographies>
            {model.dots.map((d) => (
              <Marker key={d.country} coordinates={d.at}>
                <circle r={3.5} fill={fillOf(d.shade)} fillOpacity={dim(d)} stroke={MAP_EDGE[mode]} strokeWidth={1.2}
                  style={{ cursor: 'pointer' }} onMouseMove={(e) => show(e, d)} onMouseLeave={() => setTip(null)}
                  onClick={() => pick(d)} />
              </Marker>
            ))}
          </ZoomableGroup>
        </ComposableMap>
        {tip && (
          <div className="dx-maptip" style={{ left: Math.min(tip.x + 12, Math.max(0, tip.w - 200)), top: tip.y + 12 }}>
            <b>{tip.info.country}</b>
            {byArea && <span>{keyLabel(loc, tip.info.area)}: {figure(tip.info.shade)}</span>}
            <span>{ctx.valueLabel(val)}: {figure(tip.info.own)}</span>
          </div>
        )}
      </div>
      <div className="dx-maplegend" aria-label={ctx.valueLabel(val)}>
        {ramp.slice(ramp.length - legendCuts.length).map((c, i) => (
          <span key={c} className="dx-mapkey">
            <i style={{ background: c }} />
            {legendCuts.length === 1 ? '> 0'
              : i === 0 ? `< ${cut(legendCuts[1])}` : i === legendCuts.length - 1 ? `≥ ${cut(legendCuts[i])}` : `${cut(legendCuts[i])}+`}
          </span>
        ))}
        <span className="dx-mapkey"><i style={{ background: MAP_NONE[mode] }} />{t('xExplorer.mapNone')}</span>
      </div>
      {model.unplaced.length > 0 && (
        <div className="dx-vnote" title={model.unplaced.map((x) => `${x.country}: ${figure(x.own)}`).join('\n')}>
          {t('xExplorer.mapUnplaced', { n: model.unplaced.length, names: model.unplaced.slice(0, 4).map((x) => x.country).join(', ') })}
        </div>
      )}
      {answerNote(ctx, result, pivot)}
    </div>
  );
});

/* ================================================================ pie and donut */
/* Part of a whole at a glance: at most six slices — the five largest and "Other" — each parted from the next by a
   ring of the surface, a share on each slice large enough to carry one. A click picks a slice for the rest of the page. */
const PieBody = React.memo(function PieBody({ v, pivot, result, ctx, xfKey }) {
  const { t, theme, actions } = ctx;
  const mode = theme === 'dark' ? 'dark' : 'light';
  const fmt = v.format || {};
  const surface = SURFACE[mode];
  const cur = result.params?.currency;
  const val = result.values[0];
  const sorted = [...pivot.rows].filter((r) => (r.total?.[0] ?? 0) > 0).sort((a, b) => (b.total[0] || 0) - (a.total[0] || 0));
  const keep = sorted.length > 6 ? sorted.slice(0, 5) : sorted;
  const rest = sorted.length > 6 ? sorted.slice(5) : [];
  const grand = sorted.reduce((a, r) => a + (r.total[0] || 0), 0);
  const data = keep.map((r, i) => ({
    name: pivot.R ? r.vals.map((x, j) => keyLabel(result.rows[j], x)).join(' · ') : t('xExplorer.total'),
    value: r.total[0], color: SERIES[mode][i], row: r,
  }));
  if (rest.length) data.push({ name: t('xExplorer.other'), value: rest.reduce((a, r) => a + (r.total[0] || 0), 0), color: OTHER_COLOR[mode], row: null });
  /* recharts hands a sector's click its drawn item, the entry under .payload. */
  const pick = (d) => {
    const row = d?.payload?.row ?? d?.row;
    if (!row || !pivot.R) return;
    if (v.drillMode) actions.drill(v.id, 0, row.vals[0]);
    else actions.point(v.id, row.vals, result.rows);
  };
  /* A slice's share is written beside it only when the visual asks for data labels; otherwise the legend carries
     every name with its share, as the Sales Report's mixes read. */
  const sliceLabel = ({ x, y, textAnchor, percent }) => (percent >= 0.06 ? (
    <text x={x} y={y} textAnchor={textAnchor} dominantBaseline="central" fontSize={11} fill="var(--portal-text-muted)">
      {`${Math.round(percent * 100)}%`}
    </text>
  ) : null);
  const share = (x) => (grand ? x / grand : 0);
  const legendAt = fmt.legend || 'right';
  const showLabels = fmt.labels === true;
  const negatives = pivot.rows.some((r) => (r.total?.[0] ?? 0) < 0);
  const dimmed = (d) => xfKey && d.row && d.row.key !== xfKey;
  const pct = (x) => `${(share(x) * 100).toFixed(1)}%`;
  return (
    <div className="dx-chartbox">
      <div className={`dx-piebox at-${legendAt}`}>
        <div className="dx-piearea">
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
            <PieChart margin={showLabels ? { top: 18, right: 22, bottom: 18, left: 22 } : { top: 4, right: 4, bottom: 4, left: 4 }}>
              <Tooltip formatter={(x, n) => [`${fmtValue(x, val, cur, fmt)} · ${fmtValue(share(x), { fmt: 'pct' }, cur)}`, n]}
                contentStyle={{ background: 'var(--portal-surface)', border: '1px solid var(--portal-border)', borderRadius: 8, fontSize: 12 }}
                itemStyle={{ color: 'var(--portal-text)' }} wrapperStyle={{ zIndex: 30 }} />
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={v.type === 'donut' ? '62%' : 0} outerRadius={showLabels ? '80%' : '94%'}
                stroke={surface} strokeWidth={2} isAnimationActive={false} onClick={pick}
                label={showLabels ? sliceLabel : false} labelLine={false}>
                {data.map((d) => (
                  <Cell key={d.name} fill={d.color} fillOpacity={dimmed(d) ? (mode === 'dark' ? 0.45 : 0.3) : 1}
                    style={d.row ? { cursor: 'pointer' } : undefined} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {/* A donut's hole carries the whole it is a part of. */}
          {v.type === 'donut' && grand > 0 && (
            <div className="dx-piehole" title={fmtValue(grand, val, cur)}>
              <b>{val.fmt === 'pct' ? fmtValue(grand, val, cur) : compact(grand, 1)}</b>
              <span>{t('xExplorer.total')}</span>
            </div>
          )}
        </div>
        {legendAt !== 'none' && (
          <ul className="dx-pielegend" aria-label={t('xExplorer.fmtLegend')}>
            {data.map((d) => (
              <li key={d.name} className={dimmed(d) ? 'dim' : undefined}>
                <button type="button" disabled={!d.row} onClick={() => pick(d)} title={`${d.name}: ${fmtValue(d.value, val, cur, fmt)}`}>
                  <i style={{ background: d.color }} />
                  <span className="nm">{d.name}</span>
                  <span className="pc">{pct(d.value)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {negatives && <div className="dx-vnote">{t('xExplorer.pieNegatives')}</div>}
    </div>
  );
});

/* ================================================================ KPI */
/* One figure against the same days last year: the figure, the change as an arrow AND words (a colour alone is not a
   state), last year's figure, and — with a trend axis — the two years as a small line under it. */
const KpiBody = React.memo(function KpiBody({ v, pivot, result, ctx, fmt }) {
  const { t, theme } = ctx;
  const mode = theme === 'dark' ? 'dark' : 'light';
  const cur = result.params?.currency;
  const val = result.values[0];
  const now = pivot.grand?.[0] ?? null;
  const ly = pivot.grand?.[1] ?? null;
  const ch = now !== null && ly ? (now - ly) / Math.abs(ly) : null;
  const up = ch !== null && ch >= 0;
  const big = fmt.units ? fmtValue(now, val, cur, fmt) : (val.fmt !== 'pct' && Math.abs(now) >= 100000 ? headline(now, val, cur) : fmtValue(now, val, cur));
  const trend = pivot.R ? [...pivot.rows].sort((a, b) => String(a.vals[0]).localeCompare(String(b.vals[0]), 'en', { numeric: true }))
    .map((r) => ({ cat: keyLabel(result.rows[0], r.vals[0]), now: r.total?.[0] ?? null, ly: r.total?.[1] ?? null })) : [];
  const good = mode === 'dark' ? '#0ca30c' : '#006300';
  const bad = '#d03b3b';
  return (
    <div className="dx-kpi">
      <div className="dx-kpi-n" title={fmtValue(now, val, cur)}>{now === null ? '(Blank)' : big}</div>
      <div className="dx-kpi-l">{ctx.valueLabel(val)}</div>
      {ch === null ? (
        <div className="dx-kpi-d none">{t('xExplorer.kpiNoLy')}</div>
      ) : (
        <div className="dx-kpi-d" style={{ color: up ? good : bad }}>
          {up ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
          <b>{`${up ? '+' : '−'}${(Math.abs(ch) * 100).toFixed(1)}%`}</b>
          <span>{t('xExplorer.kpiVsLy', { value: compact(ly, 2) })}</span>
        </div>
      )}
      {trend.length > 1 && (
        <div className="dx-kpi-trend">
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
            <LineChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <Tooltip formatter={(x, n) => [fmtValue(x, val, cur), n]} labelStyle={{ color: 'var(--portal-text)' }}
                contentStyle={{ background: 'var(--portal-surface)', border: '1px solid var(--portal-border)', borderRadius: 8, fontSize: 11 }}
                itemStyle={{ color: 'var(--portal-text)' }} wrapperStyle={{ zIndex: 30 }} />
              <XAxis dataKey="cat" hide />
              <Line type="monotone" dataKey="ly" name={t('xExplorer.calc_py')} stroke={OTHER_COLOR[mode]} strokeWidth={1.5}
                strokeDasharray="4 3" dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="now" name={ctx.valueLabel(val)} stroke={SERIES[mode][0]} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
});

/* ================================================================ slicer */
/* The dropdown's list is drawn over the page (a portal, fixed under the button): the visual's own box clips. It closes
   on a click elsewhere and on Escape; when the canvas scrolls it follows its button, and closes once the button has
   left the screen. (Closing on any scroll lost the list to a scroll still finishing from the moment it was opened.) */
const SlicerDrop = ({ summary, on, title, label, children }) => {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null);
  const btn = useRef(null);
  const pop = useRef(null);
  const place = () => {
    if (!btn.current) return false;
    const r = btn.current.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) return false;
    const h = Math.min(340, window.innerHeight - 24);
    const below = window.innerHeight - r.bottom - 8;
    setAt({ left: Math.min(r.left, window.innerWidth - Math.max(r.width, 240) - 8), width: Math.max(r.width, 240),
      ...(below >= Math.min(h, 220) ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }), maxHeight: h });
    return true;
  };
  useLayoutEffect(() => { if (open) place(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!pop.current?.contains(e.target) && !btn.current?.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') { setOpen(false); btn.current?.focus(); } };
    const scroll = (e) => { if (!pop.current?.contains(e.target) && !place()) setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    document.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
      document.removeEventListener('scroll', scroll, true);
    };
  }, [open]);
  return (
    <div className="dx-slicer dx-slicer-drop">
      <button type="button" ref={btn} className={`dx-slicer-btn${on ? ' on' : ''}`} aria-haspopup="listbox" aria-expanded={open}
        aria-label={`${label}: ${summary}`} title={title || summary} onClick={() => setOpen((x) => !x)}>
        <span>{summary}</span><ChevronDown size={14} />
      </button>
      {open && at && createPortal(
        <div ref={pop} className="dx-slicer-pop" style={{ position: 'fixed', ...at }}
          onPointerDown={(e) => e.stopPropagation()}>{children}</div>, document.body,
      )}
    </div>
  );
};

const SlicerBody = React.memo(function SlicerBody({ v, ctx }) {
  const { t, actions } = ctx;
  const d = v.rows[0];
  const f = ctx.fields.find((x) => x.key === d.field);
  const isRange = f?.kind === 'date' && !d.grain;
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const sel = v.slicer || {};
  const { fetchValues } = ctx;
  useEffect(() => {
    if (isRange) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      fetchValues(d.field, d.grain, q).then((r) => { if (live) setList(r); }).catch(() => { if (live) setList({ values: [] }); });
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [d.field, d.grain, q, isRange, fetchValues]);
  if (isRange) {
    return (
      <div className="dx-slicer">
        <div className="dx-slicer-range">
          <input type="date" value={sel.from || ''} aria-label={t('xExplorer.fFrom')} onChange={(e) => actions.slicer(v.id, { from: e.target.value, to: sel.to || '' })} />
          <span>–</span>
          <input type="date" value={sel.to || ''} aria-label={t('xExplorer.fTo')} onChange={(e) => actions.slicer(v.id, { from: sel.from || '', to: e.target.value })} />
        </div>
        {/* A preset is kept as its NAME, so "This month" stays this month after the report is saved. */}
        <select className="dx-slicer-preset" value={sel.period && sel.period !== 'lastN' ? sel.period : ''} aria-label={t('xExplorer.presets')}
          onChange={(e) => { const p = e.target.value; if (!p) return; actions.slicer(v.id, p === 'all' ? null : { period: p }); }}>
          <option value="">{sel.period === 'lastN' ? relLabel(sel, t) : t('xExplorer.presets')}</option>
          {REL_PERIODS.map((p) => <option key={p} value={p}>{t(`xExplorer.rel_${p}`)}</option>)}
          <option value="all">{t('xExplorer.fAll')}</option>
        </select>
      </div>
    );
  }
  const picked = new Set(sel.values || []);
  const toggle = (x) => {
    const n = new Set(picked);
    if (n.has(x)) n.delete(x); else n.add(x);
    actions.slicer(v.id, n.size ? { values: [...n] } : null);
  };
  const listBody = (
    <>
      <div className="dx-search sm">
        <Search size={13} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xExplorer.fSearch')} aria-label={t('xExplorer.fSearch')} />
      </div>
      <div className="dx-slicer-list">
        <label className="dx-vrow all">
          <input type="checkbox" checked={!picked.size} onChange={() => actions.slicer(v.id, null)} />
          <span className="dx-vname">{t('xExplorer.slicerAll')}</span>
        </label>
        {!list && <Loader2 size={13} className="dx-spin" />}
        {list?.values.map((x) => (
          <label key={x.v} className="dx-vrow">
            <input type="checkbox" checked={picked.has(x.v)} onChange={() => toggle(x.v)} />
            <span className="dx-vname">{keyLabel(d, x.v)}</span>
          </label>
        ))}
        {list?.more && <div className="dx-muted dx-slicer-more">{t('xExplorer.fMore')}</div>}
      </div>
    </>
  );
  /* A DROPDOWN slicer (the Format tab) is one line — what is chosen — and opens the same list over the page, so a row
     of filters can sit above the figures instead of two tall lists beside them. */
  if ((v.format || {}).slicerStyle === 'dropdown') {
    const names = [...picked].map((x) => keyLabel(d, x));
    const summary = !names.length ? t('xExplorer.slicerAll') : names.length <= 2 ? names.join(', ') : t('xExplorer.slicerPicked', { n: names.length });
    return <SlicerDrop summary={summary} on={names.length > 0} title={names.join(', ')} label={ctx.fieldLabel(d.field)}>{listBody}</SlicerDrop>;
  }
  return (
    <div className="dx-slicer">
      <div className="dx-search sm">
        <Search size={13} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xExplorer.fSearch')} aria-label={t('xExplorer.fSearch')} />
      </div>
      <div className="dx-slicer-list">
        <label className="dx-vrow all">
          <input type="checkbox" checked={!picked.size} onChange={() => actions.slicer(v.id, null)} />
          <span className="dx-vname">{t('xExplorer.slicerAll')}</span>
        </label>
        {!list && <Loader2 size={13} className="dx-spin" />}
        {list?.values.map((x) => (
          <label key={x.v} className="dx-vrow">
            <input type="checkbox" checked={picked.has(x.v)} onChange={() => toggle(x.v)} />
            <span className="dx-vname">{keyLabel(d, x.v)}</span>
          </label>
        ))}
        {list?.more && <div className="dx-muted dx-slicer-more">{t('xExplorer.fMore')}</div>}
      </div>
    </div>
  );
});

